import React from 'react';
import { connect } from 'react-redux';

// node
import Icon from '@mdi/react';
import { mdiHelpCircle, mdiAlertCircleOutline } from '@mdi/js';

// Components
import Tooltip from '../Tooltip';

// utils
import { numberFormat } from '../../utils';

// styles
import { Summary, Price } from './style';

// IDs de tipo_pagamento (tb_tipo_pagamento)
const TIPO_PAGAMENTO_CREDITO = '4';
const TIPO_PAGAMENTO_PIX = '13';

// Percentual de desconto no Pix (apenas para exibição).
const PERCENTUAL_DESCONTO_PIX = 0.05;
const PERCENTUAL_DESCONTO_RECOMPRA = 0.1;

const isItemRecompra = item => {
  if (!item) {
    return false;
  }

  if (item.fk_pedido_item_recompra) {
    return true;
  }

  return (
    Array.isArray(item.obsPedidoItem) &&
    item.obsPedidoItem.some(
      observacaoPedido =>
        observacaoPedido.obs_pedido_item &&
        observacaoPedido.obs_pedido_item.toLowerCase().trim() ===
          'comprar novamente'
    )
  );
};

const SummaryCart = ({
  summary,
  cart,
  getSaldoPayment,
  verificaMultiploPagamento,
  getTypePayment,
}) => {
  if (!summary) {
    return '';
  }

  const isCreditApplied = verificaMultiploPagamento.multiplePayments;
  const verifyTypePayment = getTypePayment.tipo_pagamento === TIPO_PAGAMENTO_CREDITO;
  const verifyTypePix = getTypePayment.tipo_pagamento === TIPO_PAGAMENTO_PIX;
  const itens =
    cart && cart.carrinho && Array.isArray(cart.carrinho.itens)
      ? cart.carrinho.itens
      : [];
  const isRecompra = itens.length > 0 && itens.every(isItemRecompra);

  const baseDescontoPix =
    (summary.impressao || 0) +
    (summary.acabamentos || 0) +
    (summary.servicos || 0);

  const descontoPixPreview = baseDescontoPix * PERCENTUAL_DESCONTO_PIX;
  const baseDescontoRecompra =
    (summary.impressao || 0) + (summary.acabamentos || 0);
  const descontoRecompraPreview =
    baseDescontoRecompra * PERCENTUAL_DESCONTO_RECOMPRA;
  const resumoInformaDescontoRecompra = Object.prototype.hasOwnProperty.call(
    summary,
    'desconto_recompra'
  );
  const descontoRecompra = resumoInformaDescontoRecompra
    ? parseFloat(summary.desconto_recompra) || 0
    : descontoRecompraPreview;
  const usarTotalComFallback =
    isRecompra && !summary.cupom && !resumoInformaDescontoRecompra;
  const totalCarrinho = Math.max(
    0,
    (parseFloat(summary.total) || 0) -
      (usarTotalComFallback ? descontoRecompra : 0)
  );
  const resumoInformaDesconto = Object.prototype.hasOwnProperty.call(
    summary,
    'desconto'
  );
  const descontoAplicado = resumoInformaDesconto
    ? parseFloat(summary.desconto) || 0
    : parseFloat(summary.cupom) ||
      (isRecompra && !summary.cupom ? descontoRecompra : 0);
  const descricaoDesconto = summary.cupom
    ? 'Desconto do cupom'
    : isRecompra
      ? 'Desconto da recompra'
      : verifyTypePix
        ? 'Desconto Pix'
        : 'Desconto';

  // cliente SEM cupom mostra preview de economia
  const mostrarPreviewPix =
    !summary.cupom && !isRecompra && descontoPixPreview > 0;
  const mostrarPreviewRecompra =
    isRecompra && !summary.cupom && descontoRecompra > 0;

  // cliente COM cupom E selecionou Pix avisa sobre não-cumulatividade
  const mostrarAvisoCupomPix = summary.cupom && verifyTypePix;
  const mostrarAvisoCupomRecompra = isRecompra && summary.cupom;

  function getTotal() {
    if (getSaldoPayment.saldo >= totalCarrinho) {
      return 0;
    }
    if (totalCarrinho > getSaldoPayment.saldo) {
      return totalCarrinho - getSaldoPayment.saldo;
    }
  }

  function showDiscountCredit() {
    if (getSaldoPayment.saldo >= totalCarrinho) {
      return totalCarrinho;
    } else if (getSaldoPayment.saldo < totalCarrinho) {
      return getSaldoPayment.saldo;
    }
  }

  return (
    <Summary>
      <ul>
        {summary.impressao ? (
          <li>
            <span>SubTotal</span>
            <span>R$ {numberFormat(summary.impressao)}</span>
          </li>
        ) : (
          ''
        )}
        {summary.acrescimo.valor ? (
          <li>
            <span>
              Acréscimo
              <Tooltip
                dataId={summary.acrescimo.tipo}
                text={summary.acrescimo.descricao}
                icon={<Icon path={mdiHelpCircle} size={0.8} />}
              />
            </span>
            <span>R$ {numberFormat(summary.acrescimo.valor)}</span>
          </li>
        ) : (
          ''
        )}
        {summary.acabamentos ? (
          <li>
            <span>Acabamentos</span>
            <span>R$ {numberFormat(summary.acabamentos)}</span>
          </li>
        ) : (
          ''
        )}
        {summary.servicos ? (
          <li>
            <span>Serviços Opcionais</span>
            <span>R$ {numberFormat(summary.servicos)}</span>
          </li>
        ) : (
          ''
        )}
        {summary.peso_total ? (
          <li>
            <span>Peso Total</span>
            <span>
              {numberFormat(summary.peso_total)} {summary.unidade_peso}
            </span>
          </li>
        ) : (
          ''
        )}
        {summary.entrega ? (
          <li>
            <span>Frete</span>
            <span>R$ {numberFormat(summary.entrega)}</span>
          </li>
        ) : (
          ''
        )}
        {descontoAplicado > 0 ? (
          <li>
            <span>{descricaoDesconto}</span>
            <span>- R$ {numberFormat(descontoAplicado)}</span>
          </li>
        ) : (
          ''
        )}
        {verifyTypePayment || isCreditApplied ? (
          <li>
            <span>Desconto do saldo da carteira</span>
            <span>
              - R$ {numberFormat(showDiscountCredit())}
            </span>
          </li>
        ) : (
          ''
        )}
      </ul>

      <Price>
        <span>Total</span>
        {verifyTypePayment || isCreditApplied ? (
          <span>R$ {numberFormat(getTotal())}</span>
        ) : (
          <span>R$ {numberFormat(totalCarrinho)}</span>
        )}
      </Price>

      {mostrarPreviewRecompra ? (
        <div className="recompra-suggestion">
          <span role="img" aria-label="coração">🧡</span>
          <span className="suggestion-text">
            {' '}Na <strong>recompra</strong> você economiza{' '}
            <strong>R$ {numberFormat(descontoRecompra)}</strong> (10% OFF){' '}
          </span>
          <span role="img" aria-label="coração">💙</span>
        </div>
      ) : (
        ''
      )}

      {mostrarPreviewPix ? (
        <div className="pix-suggestion">
          <span role="img" aria-label="coração">🧡</span>
          <span className="suggestion-text">
            {' '}Pagando no <strong>Pix</strong> você economiza{' '}
            <strong>R$ {numberFormat(descontoPixPreview)}</strong> (5% OFF){' '}
          </span>
          <span role="img" aria-label="coração">💙</span>
        </div>
      ) : (
        ''
      )}

      {mostrarAvisoCupomPix ? (
        <div className="pix-cupom-warning">
          <Icon path={mdiAlertCircleOutline} size={0.85} />
          <span className="warning-text">
            Você utilizou um cupom de desconto. O benefício de 5% no{' '}
            <strong>Pix</strong> é <strong>não acumulativo</strong> e não será aplicado neste pedido.
          </span>
        </div>
      ) : (
        ''
      )}

      {mostrarAvisoCupomRecompra ? (
        <div className="recompra-cupom-warning">
          <Icon path={mdiAlertCircleOutline} size={0.85} />
          <span className="warning-text">
            Você utilizou um cupom de desconto. O benefício de 10% da{' '}
            <strong>recompra</strong> é <strong>não acumulativo</strong> e não será aplicado neste pedido.
          </span>
        </div>
      ) : (
        ''
      )}

      <a
        className='prazo-link'
        target="_blank"
        rel="noopener noreferrer"
        href="https://instrucoes.fabricadolivro.com.br/perguntas-frequentes/como-contar-o-prazo-ate-receber-os-meus-produtos/"
      >
        Como calcular o prazo
      </a>
    </Summary>
  );
};

const mapStateToProps = state => {
  return {
    summary: state.cart.summaryCart,
    cart: state.cart.data,
    getSaldoPayment: state.payment.payments,
    getTypePayment: state.payment.payment,
    verificaMultiploPagamento: state.payment,
  };
};

export default connect(mapStateToProps, null)(SummaryCart);
